// src/components/admin/AdminOverview.js
import React, { useState, useEffect } from 'react';
import {
  getAdminStats,
  getFitnessVsLekhiDayTotals,
  getSundayTestsThisWeekCount,
  backfillRankScores
} from '../../services/firebase/firestoreService';

export default function AdminOverview() {
  const [stats, setStats] = useState({
    totalUsers: 0, activeUsers: 0, atRiskCount: 0,
    yesterdayRate: 0, villageCount: 0,
    totalPoints: 0, avgStreak: 0, streakUsersCount: 0,
    notifRate: 0, day1Rate: 0
  });
  const [loading, setLoading] = useState(true);

  const [dayTotals, setDayTotals] = useState(null);
  const [dayTotalsLoading, setDayTotalsLoading] = useState(false);

  const [testsThisWeek, setTestsThisWeek] = useState(null);
  const [testsLoading, setTestsLoading] = useState(false);

  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getAdminStats();
        setStats(data);
      } catch (e) {
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const loadDayTotals = async () => {
    setDayTotalsLoading(true);
    try {
      const data = await getFitnessVsLekhiDayTotals();
      setDayTotals(data);
    } catch (e) {
    } finally {
      setDayTotalsLoading(false);
    }
  };

  const loadTestsThisWeek = async () => {
    setTestsLoading(true);
    try {
      const count = await getSundayTestsThisWeekCount();
      setTestsThisWeek(count);
    } catch (e) {
    } finally {
      setTestsLoading(false);
    }
  };

  const runBackfillRankScores = async () => {
    if (!window.confirm('Run rankScore backfill for all users? Safe to re-run.')) return;
    setBackfillLoading(true);
    try {
      const result = await backfillRankScores();
      setBackfillResult(result);
    } catch (e) {
    } finally {
      setBackfillLoading(false);
    }
  };

  const atRiskPct = stats.activeUsers > 0
    ? Math.round((stats.atRiskCount / stats.activeUsers) * 100)
    : 0;

  const engagementCards = [
    { number: `${stats.activeUsers} / ${stats.totalUsers}`, label: 'Active / Total Users', sublabel: 'Admin-marked active out of all', color: '#2563eb' },
    { number: `${stats.yesterdayRate}%`, label: "Yesterday's Completion", sublabel: 'Active users who completed', color: stats.yesterdayRate >= 60 ? '#28a745' : stats.yesterdayRate >= 40 ? '#fd7e14' : '#dc3545' },
    { number: stats.atRiskCount, label: 'At-Risk Users', sublabel: 'Active, not trained 3+ days', color: atRiskPct > 20 ? '#dc3545' : '#fd7e14' },
  ];

  const habitCards = [
    { number: stats.avgStreak, label: 'Average Streak', sublabel: `Among ${stats.streakUsersCount} users with streak ≥ 1`, color: '#6366f1' },
    { number: `${stats.day1Rate}%`, label: 'Day 1 Completion', sublabel: 'Ever completed Day 1', color: '#6366f1' },
    { number: `${stats.notifRate}%`, label: 'Notification Opt-in', sublabel: 'Of active users', color: '#6366f1' },
  ];

  const scaleCards = [
    { number: stats.totalPoints, label: 'Total Points', sublabel: 'Across all users', color: '#555' },
    { number: stats.villageCount, label: 'Villages', sublabel: 'Across all users', color: '#555' },
  ];

  const sectionTitleStyle = {
    fontSize: '13px',
    fontWeight: 700,
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    margin: '20px 0 10px'
  };

  const renderCards = (cards) => (
    <div className="admin-stats-grid">
      {cards.map((card, i) => (
        <div className="admin-stat-card" key={i}>
          <div className="admin-stat-number" style={{ color: loading ? '#ccc' : card.color }}>
            {loading ? '...' : card.number}
          </div>
          <div className="admin-stat-label">{card.label}</div>
          <div style={{ fontSize: '12px', color: '#aaa', marginTop: '4px' }}>{card.sublabel}</div>
        </div>
      ))}
    </div>
  );

  return (
    <div>
      <div className="admin-section-title">Overview</div>

      <div style={sectionTitleStyle}>Engagement Today</div>
      {renderCards(engagementCards)}

      <div style={sectionTitleStyle}>Habit Strength</div>
      {renderCards(habitCards)}

      <div style={sectionTitleStyle}>Community Scale</div>
      {renderCards(scaleCards)}

      {!loading && stats.atRiskCount > 0 && (
        <div className="admin-card" style={{ marginTop: '16px' }}>
          <div className="admin-status-msg" style={{
            background: atRiskPct > 20 ? '#f8d7da' : '#fff3cd',
            color: atRiskPct > 20 ? '#721c24' : '#856404',
            margin: 0
          }}>
            ⚠️ {stats.atRiskCount} active users ({atRiskPct}%) have not trained in 3+ days.
          </div>
        </div>
      )}

      <div style={{ ...sectionTitleStyle, marginTop: '28px' }}>Deeper Numbers (on demand)</div>

      <div className="admin-card" style={{ background: '#f0f4ff', border: '1px solid #c7d2fe' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h3 style={{ margin: 0 }}>Fitness vs Lekhi Days (All-Time)</h3>
            <p style={{ fontSize: '13px', color: '#888', margin: '4px 0 0' }}>Total completed days across all users, ever.</p>
          </div>
          <button className="admin-btn admin-btn-secondary" onClick={loadDayTotals} disabled={dayTotalsLoading}>
            {dayTotalsLoading ? 'Loading...' : dayTotals ? 'Refresh' : 'Load'}
          </button>
        </div>
        {dayTotals && (
          <div style={{ display: 'flex', gap: '24px', marginTop: '16px' }}>
            <div>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#2563eb' }}>{dayTotals.fitnessDays}</div>
              <div style={{ fontSize: '12px', color: '#888' }}>Fitness Days</div>
            </div>
            <div>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#6366f1' }}>{dayTotals.lekhiDays}</div>
              <div style={{ fontSize: '12px', color: '#888' }}>Lekhi Days</div>
            </div>
          </div>
        )}
      </div>

      <div className="admin-card" style={{ background: '#f0f4ff', border: '1px solid #c7d2fe' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h3 style={{ margin: 0 }}>Sunday Tests This Week</h3>
            <p style={{ fontSize: '13px', color: '#888', margin: '4px 0 0' }}>Tests submitted since this Sunday.</p>
          </div>
          <button className="admin-btn admin-btn-secondary" onClick={loadTestsThisWeek} disabled={testsLoading}>
            {testsLoading ? 'Loading...' : testsThisWeek !== null ? 'Refresh' : 'Load'}
          </button>
        </div>
        {testsThisWeek !== null && (
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#2563eb', marginTop: '16px' }}>
            {testsThisWeek}
          </div>
        )}
      </div>

      <div className="admin-card" style={{ background: '#fff3cd', border: '1px solid #ffe69c' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h3 style={{ margin: 0 }}>Backfill Rank Scores (Streak Leaderboard)</h3>
            <p style={{ fontSize: '13px', color: '#856404', margin: '4px 0 0' }}>
              One-time setup for the streak leaderboard. Safe to re-run anytime. Remove this card once confirmed working.
            </p>
          </div>
          <button className="admin-btn admin-btn-secondary" onClick={runBackfillRankScores} disabled={backfillLoading}>
            {backfillLoading ? 'Running...' : 'Run Backfill'}
          </button>
        </div>
        {backfillResult && (
          <div style={{ fontSize: '14px', color: '#856404', marginTop: '12px' }}>
            Updated {backfillResult.updated} of {backfillResult.total} users.
          </div>
        )}
      </div>
    </div>
  );
}